<script lang="ts">
  import { navigate } from 'svelte-routing';
  import Readout from '../charts/Readout.svelte';
  import { api } from '../../api/client';
  import { addDays } from '../../utils/chart';
  import { calendar, hours, today } from '../../utils/insights';
  import { SLEEP_COLOR, STEPS_COLOR, sleepTime } from '../../utils/health';
  import { weekStart } from '../../utils/progress';
  import { shade, yearGrid, type CalendarMeasure, type YearCalendar } from '../../utils/training';
  import { locale, month, t, weekday } from '../../i18n/index.ts';

  // YearCalendar is the year at a glance on Progress (spec 006 FR-093): a
  // square a day in week columns and weekday rows from the member's first
  // day of the week, shaded in five steps relative to the year's highest and
  // blank without data, for steps, activity time, or sleep. As every chart
  // does (spec 010 FR-033), hovering a square reads it, and tapping one
  // or moving with the arrow keys selects it, whose button opens its day:
  // Health's for steps and sleep, and for activity time the training
  // log's, through onmoving.
  let { onmoving }: { onmoving: (day: string) => void } = $props();

  const MEASURES: { id: CalendarMeasure; name: string; color: string }[] = [
    { id: 'steps', name: t('activitiesYearSteps'), color: STEPS_COLOR },
    { id: 'moving', name: t('activitiesYearActivityTime'), color: 'var(--theme-accent)' },
    { id: 'sleep', name: t('activitiesYearSleep'), color: SLEEP_COLOR },
  ];
  const ws = weekStart(navigator.language);
  const thisYear = Number(today().slice(0, 4));

  let measure = $state<CalendarMeasure>('steps');
  let year = $state(thisYear);
  let cal = $state<YearCalendar | null>(null);
  let hover = $state<string | null>(null);
  let picked = $state<string | null>(null);
  let gridEl: HTMLElement | undefined = $state();
  let scroller: HTMLElement | undefined = $state();

  // A narrow window shows part of the year, so it opens this year's at today.
  $effect(() => {
    if (scroller && year === thisYear) scroller.scrollLeft = scroller.scrollWidth;
  });
  let error = $state('');

  $effect(() => {
    const m = measure, y = year;
    if (picked && !picked.startsWith(String(y))) picked = null;
    api.get<YearCalendar>(`/me/calendar?year=${y}&measure=${m}&${calendar()}`)
      .then((got) => { if (got.year === year && got.measure === measure) { cal = got; error = ''; } })
      .catch((e) => (error = e.message));
  });

  let grid = $derived(yearGrid(year, ws));
  let values = $derived(new Map((cal && cal.year === year && cal.measure === measure ? cal.days : []).map((d) => [d.date, d.value])));
  let color = $derived(MEASURES.find((m) => m.id === measure)!.color);
  // months label the column holding each month's first day.
  let months = $derived(grid.map((col) => {
    const first = col.find((d) => d?.endsWith('-01'));
    return first ? month(new Date(`${first}T12:00:00`), true) : '';
  }));
  let rows = $derived(Array.from({ length: 7 }, (_, r) => weekday(new Date(2023, 0, 1 + ((ws + r) % 7), 12), true)));

  function fill(step: number): string {
    if (step < 0) return 'transparent';
    if (step === 0) return 'var(--theme-surface1)';
    return `color-mix(in srgb, ${color} ${[0, 30, 55, 78, 100][step]}%, var(--theme-surface0))`;
  }

  function valueText(v: number): string {
    switch (measure) {
      case 'steps': return t('activitiesYearStepCount', { n: Math.round(v) });
      case 'moving': return hours(v);
      default: return sleepTime(v);
    }
  }

  const longDay = new Intl.DateTimeFormat(locale(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  function label(day: string): string {
    const v = values.get(day);
    const d = longDay.format(new Date(`${day}T12:00:00`));
    return v == null ? t('activitiesYearDayNoData', { day: d }) : t('activitiesDayValue', { day: d, value: valueText(v) });
  }

  // focusDay is the one square in the tab order: the selected day, else
  // the year's last day so far.
  let focusDay = $derived(picked ?? (year === thisYear ? today() : `${year}-12-31`));

  // key moves the selection a week across or a day down, staying in the year
  // and out of the future, and focuses the new square.
  function key(e: KeyboardEvent, day: string) {
    const by: Record<string, number> = { ArrowLeft: -7, ArrowRight: 7, ArrowUp: -1, ArrowDown: 1 };
    if (e.key === 'Escape') {
      picked = null;
      e.preventDefault();
      return;
    }
    if (!(e.key in by)) return;
    e.preventDefault();
    const next = addDays(day, by[e.key]);
    if (!next.startsWith(String(year)) || next > today()) return;
    picked = next;
    gridEl?.querySelector<HTMLElement>(`[data-day="${next}"]`)?.focus();
  }

  function open(day: string) {
    if (measure === 'moving') onmoving(day);
    else navigate(`/activities/health?date=${day}`);
  }
</script>

<section class="card p-4 space-y-3" aria-labelledby="year-heading">
  <div class="flex flex-wrap items-center justify-between gap-2">
    <h2 id="year-heading" class="font-semibold">{t('activitiesYear')}</h2>
    <div class="flex items-center gap-1 text-sm">
      <button class="btn btn-secondary btn-sm" aria-label={t('activitiesYearBefore')} onclick={() => year--}><i aria-hidden="true" class="las la-chevron-left"></i></button>
      <span class="px-1 font-medium" aria-live="polite">{year}</span>
      <button class="btn btn-secondary btn-sm" aria-label={t('activitiesYearAfter')} disabled={year >= thisYear} onclick={() => year++}><i aria-hidden="true" class="las la-chevron-right"></i></button>
    </div>
  </div>
  <div class="flex gap-1" role="group" aria-label={t('activitiesYearMeasure')}>
    {#each MEASURES as m (m.id)}
      <button class="btn btn-sm {measure === m.id ? 'btn-primary' : 'btn-secondary'}" aria-pressed={measure === m.id} onclick={() => (measure = m.id)}>{m.name}</button>
    {/each}
  </div>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  <Readout title={hover ?? picked ? label((hover ?? picked)!) : t('activitiesYearHint')} idle={!(hover ?? picked)} items={[]}>
    {#snippet action()}
      {#if picked}
        <button class="btn btn-secondary btn-sm" onclick={() => open(picked!)}>{measure === 'moving' ? t('activitiesOpenInLog') : t('activitiesOpenOnHealth')}</button>
      {/if}
    {/snippet}
  </Readout>
  <div bind:this={scroller} class="overflow-x-auto pb-1">
    <div bind:this={gridEl} class="inline-flex gap-[3px] text-xs text-text-muted" role="group" aria-label={t('activitiesYearDays', { year: String(year) })} onmouseleave={() => (hover = null)}>
      <div class="flex flex-col gap-[3px] pr-1 pt-4" aria-hidden="true">
        {#each rows as r, i (i)}<span class="h-[11px] leading-[11px] text-[11px]">{i % 2 === 1 ? r : ''}</span>{/each}
      </div>
      {#each grid as col, c (c)}
        <div class="flex flex-col gap-[3px]">
          <span class="h-[13px] leading-[13px] text-[11px] whitespace-nowrap overflow-visible w-[11px]" aria-hidden="true">{months[c]}</span>
          {#each col as day, r (r)}
            {#if day && day <= today()}
              {@const step = shade(values.get(day), cal?.max ?? 0)}
              <button class="w-[11px] h-[11px] rounded-[2px] hover:ring-1 {picked === day ? 'ring-2' : ''}" data-day={day} tabindex={day === focusDay ? 0 : -1}
                style="background: {fill(step)}; {step < 0 ? 'box-shadow: inset 0 0 0 1px var(--theme-surface1);' : ''} --tw-ring-color: var(--theme-text);"
                aria-label={label(day)} aria-pressed={picked === day} onmouseenter={() => (hover = day)} onclick={() => (picked = day)} onkeydown={(e) => key(e, day)}></button>
            {:else}
              <span class="w-[11px] h-[11px]" aria-hidden="true"></span>
            {/if}
          {/each}
        </div>
      {/each}
    </div>
  </div>
  <div class="flex flex-wrap items-center justify-end gap-2 text-xs text-text-muted">
    <span class="flex items-center gap-1" aria-hidden="true">
      {t('activitiesYearNoData')} <span class="w-[11px] h-[11px] rounded-[2px]" style="box-shadow: inset 0 0 0 1px var(--theme-surface1);"></span>
      <span class="ml-2">{t('activitiesYearLess')}</span>
      {#each [0, 1, 2, 3, 4] as s (s)}<span class="w-[11px] h-[11px] rounded-[2px]" style="background: {fill(s)};"></span>{/each}
      {t('activitiesYearMore')}
    </span>
  </div>
</section>
